import {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Check, Headphones, Volume2} from 'lucide-react';
import {useMediaDevices} from '@livekit/components-react';
import {loadDevicePreferences, saveDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {toDeviceOptions} from '../../groupCalls/services/mediaDevices';
import {matrixSurfaceMessages as m} from '../matrix/matrixMessages';

const HEADSET = /head|ear|airpod|buds|bluetooth/i;

export const OutputPicker = ({onSelect, current}: {onSelect: (deviceId: string) => void; current?: string}) => {
    const intl = useIntl();
    const devices = useMediaDevices({kind: 'audiooutput'});
    const options = useMemo(() => [{deviceId: '', label: intl.formatMessage(m.systemDefault)}, ...toDeviceOptions(devices, intl.formatMessage(m.outputTitle))], [devices, intl]);
    const [selected, setSelected] = useState(() => (current ?? loadDevicePreferences().audioOutputId) ?? '');

    const choose = (deviceId: string) => {
        setSelected(deviceId);
        if (current === undefined) {
            saveDevicePreferences({...loadDevicePreferences(), audioOutputId: deviceId || undefined});
        }
        onSelect(deviceId);
    };

    return (
        <div role="listbox" aria-label={intl.formatMessage(m.outputTitle)} className="flex flex-col gap-0.5" data-testid="call-output-picker">
            {options.map(option => {
                const active = option.deviceId === selected;
                const Icon = HEADSET.test(option.label) ? Headphones : Volume2;
                return (
                    <button
                        key={option.deviceId || 'default'}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => choose(option.deviceId)}
                        className={`flex h-11 items-center gap-3 rounded-lg px-3 text-left text-sm ${active ? 'bg-[var(--phq-call-brand-tint)] font-semibold' : 'font-medium hover:bg-[var(--phq-call-surface-muted)]'}`}
                    >
                        <Icon size={20} className={active ? 'text-[var(--phq-call-brand-ink)]' : 'text-[var(--phq-call-ink-muted)]'} aria-hidden="true"/>
                        <span className="min-w-0 flex-1 truncate">{option.label}</span>
                        {active && <Check size={18} className="text-[var(--phq-call-brand-ink)]" aria-hidden="true"/>}
                    </button>
                );
            })}
        </div>
    );
};
